<template>
  <div class="novidades-detail">
    <div class="novidades-detail__topbar">
      <div class="novidades-detail__topbar-left">
        <button
          type="button"
          class="novidades-detail__back-btn"
          @click="$emit('back')"
        >
          <v-icon x-small>fas fa-arrow-left</v-icon>
          Voltar para Novidades
        </button>
        <nav class="novidades-detail__breadcrumb" aria-label="Breadcrumb">
          <span>Início</span>
          <span>/</span>
          <span>Novidades</span>
          <span>/</span>
          <span class="novidades-detail__breadcrumb-current">{{
            novidade.descricao
          }}</span>
        </nav>
      </div>
      <div class="novidades-detail__topbar-actions">
        <button
          type="button"
          class="novidades-detail__action-btn"
          :class="{ 'novidades-detail__action-btn--success': copied }"
          @click="copyLink"
        >
          <v-icon x-small>{{ copied ? "fas fa-check" : "far fa-copy" }}</v-icon>
          {{ copied ? "Link Copiado!" : "Copiar Link" }}
        </button>
        <button
          type="button"
          class="novidades-detail__export-pdf-btn"
          :disabled="exportingPdf"
          @click="exportPdf"
        >
          <v-icon x-small>fas fa-file-download</v-icon>
          {{ exportingPdf ? "Gerando..." : "Exportar PDF" }}
        </button>
      </div>
    </div>

    <div class="novidades-detail__grid">
      <div class="novidades-detail__main">
        <article class="novidades-detail__hero">
          <div
            class="novidades-detail__hero-glow"
            :style="{ backgroundColor: scheme.dotColor }"
          />
          <div style="position:relative">
            <div class="novidades-detail__hero-meta">
              <span
                class="novidades-portal__color-badge novidades-portal__color-badge--dot-only"
                :style="badgeStyle"
                :title="scheme.name"
                :aria-label="`Cor do release: ${scheme.name}`"
              >
                <span
                  class="novidades-portal__dot"
                  :style="{ backgroundColor: scheme.dotColor }"
                />
              </span>
              <span class="novidades-portal__card-dates">
                <v-icon x-small class="mr-1">far fa-calendar-alt</v-icon>
                {{ dateFormatted.full }}
              </span>
              <span>·</span>
              <span class="novidades-portal__card-dates">
                <v-icon x-small class="mr-1">far fa-clock</v-icon>
                {{ readingLabel }}
              </span>
            </div>

            <div class="novidades-detail__hero-title-row">
              <div class="novidades-detail__hero-icon" :style="iconBoxStyle">
                <v-icon small color="white">{{ iconClass }}</v-icon>
              </div>
              <h1 class="novidades-detail__hero-title">
                {{ novidade.descricao }}
              </h1>
            </div>

            <div
              v-if="novidade.resumo"
              class="novidades-detail__executive-summary"
            >
              <strong>Resumo Executivo: </strong>{{ novidade.resumo }}
            </div>

            <div
              v-if="novidade.palavras_chave && novidade.palavras_chave.length"
              class="novidades-detail__tags-row"
            >
              <span class="novidades-portal__filter-label mr-1">
                <v-icon x-small>fas fa-tag</v-icon> Tags:
              </span>
              <button
                v-for="tag in novidade.palavras_chave"
                :key="tag"
                type="button"
                class="novidades-detail__tag-btn"
                @click="$emit('select-tag', tag)"
              >
                #{{ tag }}
              </button>
            </div>
          </div>
        </article>

        <div>
          <div class="novidades-detail__section-head mb-3">
            <div>
              <h2 class="novidades-detail__section-title">
                <v-icon small color="#059669">fas fa-magic</v-icon>
                Melhorias e Atualizações Incluídas
              </h2>
              <p class="novidades-detail__section-sub">
                Confira cada aprimoramento em detalhes e seu impacto na sua
                operação
              </p>
            </div>
            <span class="novidades-detail__count-badge">
              {{ improvements.length }} itens
            </span>
          </div>

          <div class="d-flex flex-column" style="gap:16px">
            <ImprovementCard
              v-for="(imp, idx) in improvements"
              :key="imp.id || idx"
              :improvement="imp"
              :index="idx"
            />
          </div>
        </div>
      </div>

      <aside class="novidades-detail__sidebar">
        <NovidadesResourceIndex :items="improvements" />
      </aside>
    </div>
  </div>
</template>

<script>
import ImprovementCard from "./ImprovementCard";
import NovidadesResourceIndex from "./NovidadesResourceIndex";
import Api from "@/service/api-web";
import {
  getColorScheme,
  formatDateTimeBR,
  parseImprovementsFromContent,
  resolveNovidadeIcon,
  estimateReadingMinutes,
  buildNovidadeShareUrl
} from "@/utils/novidades";

export default {
  name: "NovidadeDetailView",
  components: {
    ImprovementCard,
    NovidadesResourceIndex
  },
  props: {
    novidade: {
      type: Object,
      required: true
    },
    isDark: {
      type: Boolean,
      default: false
    },
    unidadeNome: {
      type: String,
      default: ""
    }
  },
  data: () => ({
    copied: false,
    copyTimer: null,
    exportingPdf: false
  }),
  computed: {
    scheme() {
      return getColorScheme(this.novidade.cor, { isDark: this.isDark });
    },
    badgeStyle() {
      const s = this.scheme;
      return {
        backgroundColor: s.badgeBg,
        color: s.badgeText,
        borderColor: s.badgeBorder
      };
    },
    iconBoxStyle() {
      const s = this.scheme;
      return {
        background: `linear-gradient(135deg, ${s.gradientFrom}, ${s.gradientTo})`
      };
    },
    iconClass() {
      return resolveNovidadeIcon(this.novidade.icone).class;
    },
    dateFormatted() {
      return formatDateTimeBR(this.novidade.data_publicacao);
    },
    improvements() {
      return parseImprovementsFromContent(this.novidade.conteudo || "", {
        tags: this.novidade.palavras_chave || []
      });
    },
    readingLabel() {
      const minutes = estimateReadingMinutes({
        descricao: this.novidade.descricao,
        resumo: this.novidade.resumo,
        conteudo: this.novidade.conteudo
      });
      return `${minutes} min de leitura`;
    }
  },
  beforeDestroy() {
    if (this.copyTimer) clearTimeout(this.copyTimer);
  },
  methods: {
    copyLink() {
      const url = buildNovidadeShareUrl(
        this.novidade.idnovidade,
        window.location
      );
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          this.copied = true;
          if (this.copyTimer) clearTimeout(this.copyTimer);
          this.copyTimer = setTimeout(() => {
            this.copied = false;
          }, 2500);
        });
      }
    },
    exportPdf() {
      if (this.exportingPdf || !this.novidade.idnovidade) return;
      this.exportingPdf = true;
      Api.v4.novidades.exportDetalhePdf(
          this.novidade.idnovidade,
          this.unidadeNome
        )
        .then(resp => {
          const title = `Pré-visualização do Relatório: ${this.novidade.descricao}`;
          this.$emit("pdf-ready", {
            blob: resp.data,
            filename: `novidade-${this.novidade.idnovidade}.pdf`,
            title
          });
        })
        .catch(() => {
          this.$emit(
            "export-error",
            "Não foi possível gerar o PDF desta novidade."
          );
        })
        .finally(() => {
          this.exportingPdf = false;
        });
    }
  }
};
</script>

<style lang="scss" src="../novidades-detail.scss"></style>

<style scoped>
.novidades-portal__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
</style>
